import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  FileText, Mail, MessageSquare, Link2, Send, RefreshCw, Copy,
  Bell, Clock, CheckSquare, Eye,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Textarea } from "../ui/textarea";
import { Input } from "../ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { useAuth } from "../../context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DOCUMENTI_CATALOG = [
  { key: "carta_identita_ospite",   label: "Carta d'identità ospite" },
  { key: "tessera_sanitaria",        label: "Tessera sanitaria" },
  { key: "verbale_invalidita",       label: "Verbale invalidità" },
  { key: "piano_terapeutico",        label: "Piano terapeutico" },
  { key: "relazione_sanitaria",      label: "Relazione sanitaria" },
  { key: "esami_recenti",            label: "Esami recenti" },
  { key: "impegnativa_medico",       label: "Impegnativa medico" },
  { key: "contratto_amm_sostegno",   label: "Contratto amm. di sostegno" },
  { key: "certificazione_alzheimer", label: "Certificazione Alzheimer" },
  { key: "dichiarazione_redditi",    label: "Ultima dichiarazione dei redditi" },
  { key: "altro",                    label: "Altro documento" },
];

const MODALITA_OPTIONS = [
  { value: "email",         label: "Email",         icon: Mail },
  { value: "sms",           label: "SMS",           icon: MessageSquare },
  { value: "whatsapp",      label: "WhatsApp",      icon: MessageSquare },
  { value: "link_diretto",  label: "Link diretto",  icon: Link2 },
];

export default function RichiediDocumentiDialog({ open, onOpenChange, richiesta, onSuccess }) {
  const { token } = useAuth();
  const [documentiSelezionati, setDocumentiSelezionati] = useState([]);
  const [modalita, setModalita] = useState("email");
  const [emailDest, setEmailDest] = useState("");
  const [messaggio, setMessaggio] = useState("");
  const [scadenzaGiorni, setScadenzaGiorni] = useState(30);
  const [notificaApertura, setNotificaApertura] = useState(true);
  const [notificaCaricamento, setNotificaCaricamento] = useState(true);
  const [promemoria3, setPromemoria3] = useState(true);
  const [promemoria7, setPromemoria7] = useState(false);
  const [showAnteprima, setShowAnteprima] = useState(true);
  const [loading, setLoading] = useState(false);
  const [linkGenerato, setLinkGenerato] = useState(null);
  const [uploadLinkEsistente, setUploadLinkEsistente] = useState(null);

  const nomeRef = richiesta
    ? `${richiesta.nome_referente} ${richiesta.cognome_referente}`.trim()
    : "";
  const strutturaNome = ""; // viene usato dal backend

  useEffect(() => {
    if (!open) {
      setDocumentiSelezionati([]);
      setLinkGenerato(null);
      return;
    }
    if (richiesta?.email_referente) setEmailDest(richiesta.email_referente);
    // Carica link esistente
    if (richiesta?.id) {
      axios
        .get(`${API_URL}/api/ospiti-rete/richieste/${richiesta.id}/documento-link`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => setUploadLinkEsistente(res.data.link))
        .catch(() => {});
    }
  }, [open, richiesta?.id, richiesta?.email_referente, token]);

  const toggleDoc = (key) => {
    setDocumentiSelezionati((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const messaggioPreview = () => {
    const ospite = richiesta?.nome_ospite || "del vostro caro";
    return (
      messaggio ||
      `Gentile ${nomeRef},\n\nper poter valutare correttamente l'inserimento di ${ospite} presso la nostra struttura, Le chiediamo gentilmente di caricare la documentazione indicata.\n\nPotrà caricare i documenti in modo semplice e sicuro tramite il pulsante sottostante.\n\nCordiali saluti`
    );
  };

  const handleInvia = async () => {
    if (documentiSelezionati.length === 0) {
      toast.error("Seleziona almeno un documento");
      return;
    }
    if (modalita === "email" && !emailDest.trim()) {
      toast.error("Inserisci l'indirizzo email del destinatario");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/ospiti-rete/richieste/${richiesta.id}/richiedi-documenti`,
        {
          documenti: documentiSelezionati,
          modalita,
          email_destinatario: emailDest || null,
          messaggio_personalizzato: messaggio || null,
          scadenza_giorni: scadenzaGiorni,
          notifica_apertura: notificaApertura,
          notifica_caricamento: notificaCaricamento,
          promemoria_3gg: promemoria3,
          promemoria_7gg: promemoria7,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLinkGenerato(res.data.upload_link);
      toast.success("Richiesta documenti inviata con successo");
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nell'invio della richiesta");
    } finally {
      setLoading(false);
    }
  };

  const copyLink = (link) => {
    navigator.clipboard.writeText(link).then(() => toast.success("Link copiato negli appunti"));
  };

  const scadenzaDisplay = new Date(Date.now() + scadenzaGiorni * 86400000).toLocaleDateString("it-IT");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center gap-2 text-slate-900">
            <FileText className="w-5 h-5 text-violet-600" />
            Richiedi Documenti
            {richiesta && <span className="text-slate-400 font-normal text-base">— {nomeRef}</span>}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-2">
          {/* Colonna sinistra: configurazione */}
          <div className="lg:col-span-2 space-y-5">
            {/* 1. Documenti */}
            <section>
              <h3 className="text-sm font-semibold text-slate-700 mb-3">1. Documenti da richiedere</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {DOCUMENTI_CATALOG.map((doc) => (
                  <label key={doc.key} className="flex items-center gap-2 cursor-pointer group">
                    <Checkbox
                      checked={documentiSelezionati.includes(doc.key)}
                      onCheckedChange={() => toggleDoc(doc.key)}
                    />
                    <span className="text-sm text-slate-700 group-hover:text-blue-600 transition-colors">
                      {doc.label}
                    </span>
                  </label>
                ))}
              </div>
            </section>

            {/* 2. Modalità invio */}
            <section>
              <h3 className="text-sm font-semibold text-slate-700 mb-3">2. Modalità di invio</h3>
              <div className="flex flex-wrap gap-2">
                {MODALITA_OPTIONS.map(({ value, label, icon: Icon }) => (
                  <button
                    key={value}
                    onClick={() => setModalita(value)}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium transition-all ${
                      modalita === value
                        ? "bg-blue-600 text-white border-blue-600"
                        : "bg-white text-slate-600 border-slate-200 hover:border-blue-300"
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {label}
                  </button>
                ))}
              </div>
              {modalita === "email" && (
                <div className="mt-3">
                  <Label className="text-xs">Email destinatario</Label>
                  <Input
                    type="email"
                    className="mt-1 text-sm"
                    placeholder="email@esempio.it"
                    value={emailDest}
                    onChange={(e) => setEmailDest(e.target.value)}
                  />
                </div>
              )}
            </section>

            {/* 3. Messaggio */}
            <section>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-slate-700">3. Messaggio personalizzato</h3>
                <button
                  className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                  onClick={() => setShowAnteprima(!showAnteprima)}
                >
                  <Eye className="w-3 h-3" />
                  {showAnteprima ? "Nascondi" : "Mostra"} anteprima
                </button>
              </div>
              <Textarea
                rows={4}
                className="text-sm"
                placeholder={messaggioPreview()}
                value={messaggio}
                onChange={(e) => setMessaggio(e.target.value)}
              />
              <p className="text-xs text-slate-400 mt-1">{(messaggio || messaggioPreview()).length} / 1000 caratteri</p>
            </section>

            {/* 4. Link e scadenza */}
            <section>
              <h3 className="text-sm font-semibold text-slate-700 mb-3">4. Link di caricamento</h3>
              {linkGenerato ? (
                <div className="p-3 bg-blue-50 rounded-lg flex items-center gap-2">
                  <Link2 className="w-4 h-4 text-blue-600 shrink-0" />
                  <span className="text-sm text-blue-700 truncate flex-1">{linkGenerato}</span>
                  <Button size="sm" variant="ghost" className="h-7 shrink-0" onClick={() => copyLink(linkGenerato)}>
                    <Copy className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ) : uploadLinkEsistente ? (
                <div className="p-3 bg-slate-50 rounded-lg flex items-center gap-2">
                  <Link2 className="w-4 h-4 text-slate-400 shrink-0" />
                  <span className="text-xs text-slate-500 truncate flex-1">Link esistente: {uploadLinkEsistente.upload_link}</span>
                  <Button size="sm" variant="ghost" className="h-7 shrink-0" onClick={() => copyLink(uploadLinkEsistente.upload_link)}>
                    <Copy className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ) : (
                <p className="text-sm text-slate-400 italic">Il link verrà generato all'invio</p>
              )}
              <div className="flex items-center gap-3 mt-3">
                <Label className="text-xs whitespace-nowrap">Scadenza richiesta</Label>
                <Select value={String(scadenzaGiorni)} onValueChange={(v) => setScadenzaGiorni(Number(v))}>
                  <SelectTrigger className="h-8 text-xs w-36">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[7, 14, 30, 60, 90].map((g) => (
                      <SelectItem key={g} value={String(g)} className="text-xs">{g} giorni</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-xs text-slate-400">Scadenza: {scadenzaDisplay}</span>
              </div>
            </section>

            {/* 5. Notifiche */}
            <section>
              <h3 className="text-sm font-semibold text-slate-700 mb-3">5. Notifiche e promemoria</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {[
                  { state: notificaApertura, set: setNotificaApertura, label: "Avvisa quando il familiare apre il link" },
                  { state: notificaCaricamento, set: setNotificaCaricamento, label: "Avvisa quando carica un documento" },
                  { state: promemoria3, set: setPromemoria3, label: "Promemoria automatico dopo 3 giorni" },
                  { state: promemoria7, set: setPromemoria7, label: "Promemoria automatico dopo 7 giorni" },
                ].map(({ state, set, label }) => (
                  <label key={label} className="flex items-center gap-2 cursor-pointer">
                    <Checkbox checked={state} onCheckedChange={(v) => set(!!v)} />
                    <span className="text-xs text-slate-600">{label}</span>
                  </label>
                ))}
              </div>
            </section>
          </div>

          {/* Colonna destra: anteprima + riepilogo */}
          <div className="space-y-4">
            {/* Anteprima messaggio */}
            {showAnteprima && (
              <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                <h4 className="text-xs font-semibold text-slate-600 mb-3 uppercase tracking-wide">Anteprima messaggio</h4>
                <div className="bg-white rounded-lg p-3 text-sm text-slate-700 whitespace-pre-line border border-slate-100">
                  {messaggioPreview()}
                </div>
                <Button
                  className="w-full mt-3 bg-blue-600 hover:bg-blue-700 text-white text-sm"
                  disabled
                >
                  Carica i documenti
                </Button>
                <p className="text-xs text-slate-400 text-center mt-1">Link valido fino al {scadenzaDisplay}</p>
              </div>
            )}

            {/* Riepilogo */}
            <div className="p-4 bg-white rounded-xl border border-slate-200 space-y-3">
              <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide flex items-center gap-1.5">
                <CheckSquare className="w-3.5 h-3.5" />Riepilogo richiesta
              </h4>
              <RiepilogoRow icon={FileText} label="Documenti richiesti" value={`${documentiSelezionati.length} documenti`} />
              <RiepilogoRow icon={Send} label="Modalità di invio" value={MODALITA_OPTIONS.find((m) => m.value === modalita)?.label} />
              <RiepilogoRow icon={Clock} label="Scadenza" value={`${scadenzaGiorni} giorni (${scadenzaDisplay})`} />
              {richiesta?.attivita?.length > 0 && (
                <div className="border-t border-slate-100 pt-3">
                  <p className="text-xs font-semibold text-slate-600 mb-2">Ultime attività</p>
                  {[...(richiesta.attivita || [])].slice(-3).reverse().map((a) => (
                    <p key={a.id} className="text-xs text-slate-500 mb-1">
                      {new Date(a.created_at).toLocaleDateString("it-IT")} — {a.testo}
                    </p>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Annulla
          </Button>
          {linkGenerato && (
            <Button variant="outline" onClick={() => copyLink(linkGenerato)} className="gap-1.5">
              <Copy className="w-4 h-4" />Copia link
            </Button>
          )}
          <Button
            className="bg-violet-600 hover:bg-violet-700 text-white gap-1.5"
            onClick={handleInvia}
            disabled={loading || documentiSelezionati.length === 0}
          >
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Invia richiesta
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RiepilogoRow({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-center gap-2 text-xs">
      <Icon className="w-3.5 h-3.5 text-slate-400 shrink-0" />
      <span className="text-slate-500">{label}:</span>
      <span className="text-slate-700 font-medium">{value}</span>
    </div>
  );
}
